import { createFileRoute, Link } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  signInUser,
  verifyMfa,
  completeNewPassword,
  forgotPassword,
  confirmForgotPassword,
  completeMfaSetup,
} from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { ENV } from "@/lib/config/env";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { passwordSchema } from "@/lib/auth/password-schema";
import { YesChefLogo } from "@/components/common/YesChefLogo";

// ============ Schemas ============

const loginSchema = z.object({
  email: z.string().email({ error: () => t`Invalid email address` }),
  password: z.string().min(1, { error: () => t`Password is required` }),
});

const mfaSchema = z.object({
  code: z
    .string()
    .length(6, { error: () => t`MFA code must be 6 digits` })
    .regex(/^\d+$/, { error: () => t`MFA code must contain only digits` }),
});

const newPasswordSchema = z
  .object({
    newPassword: passwordSchema,
    confirmPassword: z.string(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    error: () => t`Passwords do not match`,
    path: ["confirmPassword"],
  });

const forgotPasswordSchema = z.object({
  email: z.string().email({ error: () => t`Invalid email address` }),
});

const resetPasswordSchema = z
  .object({
    code: z.string().min(1, { error: () => t`Verification code is required` }),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    error: () => t`Passwords do not match`,
    path: ["confirmPassword"],
  });

const mfaSetupSchema = z.object({
  code: z
    .string()
    .length(6, { error: () => t`Code must be 6 digits` })
    .regex(/^\d+$/, { error: () => t`Code must contain only digits` }),
});

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
});

type LoginForm = z.infer<typeof loginSchema>;
type MfaForm = z.infer<typeof mfaSchema>;
type NewPasswordForm = z.infer<typeof newPasswordSchema>;
type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;
type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;
type MfaSetupForm = z.infer<typeof mfaSetupSchema>;

type AuthStep =
  | "login"
  | "mfa"
  | "mfa_setup"
  | "new_password"
  | "forgot_password"
  | "reset_password";

export const Route = createFileRoute("/(public)/login")({
  validateSearch: (search) => loginSearchSchema.parse(search),
  component: LoginComponent,
});

function normalizeRedirectTarget(redirect?: string): string {
  const maxDepth = 12;
  let candidate = redirect ?? "";

  for (let i = 0; i < maxDepth; i += 1) {
    if (!candidate || !candidate.startsWith("/") || candidate.startsWith("//")) {
      return "/dashboard";
    }

    if (candidate === "/login") {
      return "/dashboard";
    }

    if (candidate.startsWith("/login?")) {
      const params = new URLSearchParams(candidate.slice("/login?".length));
      const nested = params.get("redirect");
      if (!nested) {
        return "/dashboard";
      }
      candidate = nested;
      continue;
    }

    return candidate;
  }

  return "/dashboard";
}

function LoginComponent() {
  const search = Route.useSearch();
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { i18n } = useLingui();
  const redirectTarget = normalizeRedirectTarget(search.redirect);

  const redirectAfterAuth = () => {
    window.location.replace(redirectTarget);
  };

  const [step, setStep] = useState<AuthStep>("login");
  const [session, setSession] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [challengeType, setChallengeType] = useState<
    "SMS_MFA" | "SOFTWARE_TOKEN_MFA"
  >("SOFTWARE_TOKEN_MFA");
  const [mfaSetupData, setMfaSetupData] = useState<{
    secretCode?: string;
    qrCodeUri?: string;
  }>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      redirectAfterAuth();
    }
  }, [isAuthenticated, authLoading, redirectTarget, redirectAfterAuth]);

  // Login form
  const loginForm = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  // MFA form
  const mfaForm = useForm<MfaForm>({
    resolver: zodResolver(mfaSchema),
  });

  // New password form
  const newPasswordForm = useForm<NewPasswordForm>({
    resolver: zodResolver(newPasswordSchema),
  });

  // Forgot password form
  const forgotPasswordForm = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  // Reset password form
  const resetPasswordForm = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
  });

  // MFA setup form
  const mfaSetupForm = useForm<MfaSetupForm>({
    resolver: zodResolver(mfaSetupSchema),
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: signInUser,
    onSuccess: (response) => {
      if (response.success) {
        toast.success(t`Login successful`);
        queryClient.removeQueries({ queryKey: ["auth", "user"] });
        redirectAfterAuth();
      } else if (response.challengeName) {
        setSession(response.session || "");

        switch (response.challengeName) {
          case "SMS_MFA":
            setChallengeType("SMS_MFA");
            setStep("mfa");
            toast.info(t`Please enter the code sent to your phone`);
            break;
          case "SOFTWARE_TOKEN_MFA":
            setChallengeType("SOFTWARE_TOKEN_MFA");
            setStep("mfa");
            toast.info(t`Please enter your authenticator code`);
            break;
          case "MFA_SETUP":
            if (response.mfaSetup) {
              setMfaSetupData(response.mfaSetup);
            }
            setStep("mfa_setup");
            toast.info(t`Please set up MFA to continue`);
            break;
          case "NEW_PASSWORD_REQUIRED":
            setStep("new_password");
            toast.info(t`Please set a new password`);
            break;
        }
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || t`Login failed`);
    },
  });

  // MFA verification mutation
  const mfaMutation = useMutation({
    mutationFn: verifyMfa,
    onSuccess: (response) => {
      if (response.success) {
        toast.success(t`Login successful`);
        queryClient.removeQueries({ queryKey: ["auth", "user"] });
        redirectAfterAuth();
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || t`MFA verification failed`);
    },
  });

  // New password mutation
  const newPasswordMutation = useMutation({
    mutationFn: completeNewPassword,
    onSuccess: (response) => {
      if (response.success) {
        toast.success(t`Password changed successfully`);
        queryClient.removeQueries({ queryKey: ["auth", "user"] });
        redirectAfterAuth();
      } else if (response.challengeName === "MFA_SETUP") {
        if (response.mfaSetup) {
          setMfaSetupData(response.mfaSetup);
        }
        setSession(response.session || "");
        setStep("mfa_setup");
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || t`Password change failed`);
    },
  });

  // Forgot password mutation
  const forgotPasswordMutation = useMutation({
    mutationFn: forgotPassword,
    onSuccess: (response) => {
      if (response.success) {
        toast.success(
          t`Verification code sent to ${response.destination || t`your email`}`,
        );
        setStep("reset_password");
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || t`Failed to send reset code`);
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: confirmForgotPassword,
    onSuccess: () => {
      toast.success(t`Password reset successful. Please sign in.`);
      setStep("login");
      loginForm.reset();
    },
    onError: (error: Error) => {
      toast.error(error.message || t`Password reset failed`);
    },
  });

  // MFA setup mutation
  const mfaSetupMutation = useMutation({
    mutationFn: completeMfaSetup,
    onSuccess: (response) => {
      if (response.success) {
        toast.success(t`MFA setup complete`);
        queryClient.removeQueries({ queryKey: ["auth", "user"] });
        redirectAfterAuth();
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || t`MFA setup failed`);
    },
  });

  // Form submit handlers
  const onLoginSubmit = (data: LoginForm) => {
    setEmail(data.email);
    loginMutation.mutate(data);
  };

  const onMfaSubmit = (data: MfaForm) => {
    mfaMutation.mutate({
      code: data.code,
      session,
      challengeType,
      email,
    });
  };

  const onNewPasswordSubmit = (data: NewPasswordForm) => {
    newPasswordMutation.mutate({
      email,
      newPassword: data.newPassword,
      session,
      firstName: data.firstName,
      lastName: data.lastName,
    });
  };

  const onForgotPasswordSubmit = (data: ForgotPasswordForm) => {
    setEmail(data.email);
    forgotPasswordMutation.mutate({ email: data.email });
  };

  const onResetPasswordSubmit = (data: ResetPasswordForm) => {
    resetPasswordMutation.mutate({
      email,
      code: data.code,
      newPassword: data.newPassword,
    });
  };

  const onMfaSetupSubmit = (data: MfaSetupForm) => {
    mfaSetupMutation.mutate({
      code: data.code,
      session,
      email,
    });
  };

  const handleGoogleAuth = () => {
    const apiBaseUrl = ENV.VITE_API_BASE_URL;

    const redirectTo = `${window.location.origin}${redirectTarget}`;
    const authUrl = new URL(`${apiBaseUrl}/auth/google`);
    authUrl.searchParams.set("redirect", redirectTo);

    window.location.href = authUrl.toString();
  };

  const goBack = () => {
    setStep("login");
    loginForm.reset();
    mfaForm.reset();
    newPasswordForm.reset();
    forgotPasswordForm.reset();
    resetPasswordForm.reset();
    mfaSetupForm.reset();
  };

  if (authLoading || isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-(--color-surface-content)">
        <Loader2 className="h-8 w-8 animate-spin text-(--color-text-primary)" />
      </div>
    );
  }

  const isPending =
    loginMutation.isPending ||
    mfaMutation.isPending ||
    newPasswordMutation.isPending ||
    forgotPasswordMutation.isPending ||
    resetPasswordMutation.isPending ||
    mfaSetupMutation.isPending;

  return (
    <div className="flex min-h-screen bg-(--color-surface-content)">
      {/* Left side - Background Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=800&h=1000&fit=crop"
          alt={i18n._("Background")}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-6">
          {/* Login Step */}
          {step === "login" && (
            <>
              {/* Logo and Branding */}
              <div className="mb-8 flex items-center gap-2">
                <YesChefLogo />
                <span className="text-lg font-semibold text-(--color-text-primary)">
                  {i18n._("Yes, Chef!")}
                </span>
              </div>

              {/* Heading and Subtitle */}
              <div className="space-y-3 mb-8">
                <h1 className="text-4xl font-bold tracking-tight text-(--color-text-primary)">
                  {i18n._("Login to Yes, Chef!")}
                </h1>
                <p className="text-base text-(--color-text-secondary)">
                  {i18n._("Welcome back. Keep service smooth and consistent.")}
                </p>
              </div>

              <form
                onSubmit={loginForm.handleSubmit(onLoginSubmit)}
                className="space-y-5"
                noValidate
              >
                {/* Google Auth Button - FIRST */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-(--color-border) bg-(--color-surface-content) text-(--color-text-primary) hover:bg-(--color-surface-muted) h-12 font-medium"
                  disabled={isPending}
                  onClick={handleGoogleAuth}
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {i18n._("Continue with Google")}
                </Button>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-(--color-border)" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-(--color-surface-content) px-3 text-(--color-text-secondary) font-medium">
                      {i18n._("or")}
                    </span>
                  </div>
                </div>

                {/* Email Input */}
                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    className="text-sm font-medium text-(--color-text-secondary)"
                  >
                    {i18n._("Email")}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={i18n._("Enter your email address...")}
                    className="bg-(--color-surface-content) border-(--color-border) text-(--color-text-primary) placeholder:text-(--color-text-secondary) focus:border-(--color-border-strong) focus:ring-(--color-ring) h-12 text-base"
                    {...loginForm.register("email")}
                    disabled={isPending}
                  />
                  {loginForm.formState.errors.email && (
                    <p className="text-sm text-(--color-danger-text)">
                      {loginForm.formState.errors.email.message}
                    </p>
                  )}
                </div>

                {/* Password Input */}
                <div className="space-y-2">
                  <Label
                    htmlFor="password"
                    className="text-sm font-medium text-(--color-text-secondary)"
                  >
                    {i18n._("Password")}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder={i18n._("Enter your password...")}
                      className="bg-(--color-surface-content) border-(--color-border) text-(--color-text-primary) placeholder:text-(--color-text-secondary) focus:border-(--color-border-strong) focus:ring-(--color-ring) pr-10 h-12 text-base"
                      {...loginForm.register("password")}
                      disabled={isPending}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-(--color-text-secondary) hover:text-(--color-text-secondary)"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {loginForm.formState.errors.password && (
                    <p className="text-sm text-(--color-danger-text)">
                      {loginForm.formState.errors.password.message}
                    </p>
                  )}
                </div>

                {/* Forgot Password Link */}
                <div className="flex justify-end">
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
                  >
                    {i18n._("Forgot password?")}
                  </Link>
                </div>

                {/* Login Button with Arrow */}
                <Button
                  type="submit"
                  className="w-full bg-(--gray-900) text-white hover:bg-(--gray-800) font-semibold h-12 text-base"
                  disabled={isPending}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {i18n._("Signing in...")}
                    </>
                  ) : (
                    <>
                      {i18n._("Login")}
                      <span className="ml-2">→</span>
                    </>
                  )}
                </Button>
              </form>

              {/* Sign Up Link */}
              <div className="text-center pt-2">
                <p className="text-sm text-(--color-text-secondary)">
                  {i18n._("First time with Yes, Chef?")}{" "}
                  <Link
                    to="/sign-up"
                    className="font-semibold text-(--color-text-primary) hover:underline"
                  >
                    {i18n._("Sign up")}
                  </Link>
                </p>
              </div>
            </>
          )}

          {/* MFA Step */}
          {step === "mfa" && (
            <>
              <div className="space-y-2 text-center lg:text-left">
                <h1 className="text-2xl font-semibold tracking-tight text-(--color-text-primary)">
                  {i18n._("Two-factor authentication")}
                </h1>
                <p className="text-sm text-(--color-text-secondary)">
                  {challengeType === "SMS_MFA"
                    ? i18n._("Enter the code sent to your phone")
                    : i18n._("Enter the code from your authenticator app")}
                </p>
              </div>
              <form
                onSubmit={mfaForm.handleSubmit(onMfaSubmit)}
                className="space-y-4"
                noValidate
              >
                <div className="space-y-2">
                  <Label htmlFor="code" className="text-(--color-text-secondary)">
                    {i18n._("Verification code")}
                  </Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    placeholder="000000"
                    maxLength={6}
                    className="bg-(--color-surface-content) border-(--color-border) text-(--color-text-primary) text-center text-2xl tracking-[0.5em] placeholder:text-(--color-text-secondary) focus:border-(--color-border-strong) focus:ring-(--color-ring)"
                    {...mfaForm.register("code")}
                    disabled={isPending}
                  />
                  {mfaForm.formState.errors.code && (
                    <p className="text-sm text-(--color-danger-text)">
                      {mfaForm.formState.errors.code.message}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-(--gray-900) text-white hover:bg-(--gray-800) font-medium"
                  disabled={isPending}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {i18n._("Verifying...")}
                    </>
                  ) : (
                    i18n._("Verify")
                  )}
                </Button>
              </form>
              <div className="text-center">
                <button
                  type="button"
                  onClick={goBack}
                  className="text-sm text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
                >
                  {i18n._("← Back to sign in")}
                </button>
              </div>
            </>
          )}

          {/* MFA Setup Step */}
          {step === "mfa_setup" && (
            <>
              <div className="space-y-2 text-center lg:text-left">
                <h1 className="text-2xl font-semibold tracking-tight text-(--color-text-primary)">
                  {i18n._("Set up authenticator")}
                </h1>
                <p className="text-sm text-(--color-text-secondary)">
                  {i18n._("Scan the QR code with your authenticator app")}
                </p>
              </div>
              <div className="space-y-4">
                {mfaSetupData.qrCodeUri && (
                  <div className="flex flex-col items-center space-y-4">
                    <div className="p-3 bg-(--color-surface-muted) rounded-lg">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(mfaSetupData.qrCodeUri)}`}
                        alt={i18n._("MFA QR Code")}
                        className="w-32 h-32"
                      />
                    </div>
                    {mfaSetupData.secretCode && (
                      <div className="text-center space-y-1">
                        <p className="text-xs text-(--color-text-secondary)">
                          {i18n._("Or enter this code manually:")}
                        </p>
                        <code className="text-xs bg-(--color-surface-muted) px-3 py-1.5 rounded font-mono text-(--color-text-primary) border border-(--color-border)">
                          {mfaSetupData.secretCode}
                        </code>
                      </div>
                    )}
                  </div>
                )}
                <form
                  onSubmit={mfaSetupForm.handleSubmit(onMfaSetupSubmit)}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label htmlFor="setupCode" className="text-(--color-text-secondary)">
                      {i18n._("Enter code from app")}
                    </Label>
                    <Input
                      id="setupCode"
                      type="text"
                      inputMode="numeric"
                      placeholder="000000"
                      maxLength={6}
                      className="bg-(--color-surface-content) border-(--color-border) text-(--color-text-primary) text-center text-2xl tracking-[0.5em] placeholder:text-(--color-text-secondary) focus:border-(--color-border-strong) focus:ring-(--color-ring)"
                      {...mfaSetupForm.register("code")}
                      disabled={isPending}
                    />
                    {mfaSetupForm.formState.errors.code && (
                      <p className="text-sm text-(--color-danger-text)">
                        {mfaSetupForm.formState.errors.code.message}
                      </p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-(--gray-900) text-white hover:bg-(--gray-800) font-medium"
                    disabled={isPending}
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {i18n._("Verifying...")}
                      </>
                    ) : (
                      i18n._("Complete setup")
                    )}
                  </Button>
                </form>
              </div>
              <div className="text-center">
                <button
                  type="button"
                  onClick={goBack}
                  className="text-sm text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
                >
                  {i18n._("← Back to sign in")}
                </button>
              </div>
            </>
          )}

          {/* New Password Step */}
          {step === "new_password" && (
            <>
              <div className="space-y-2 text-center lg:text-left">
                <h1 className="text-2xl font-semibold tracking-tight text-(--color-text-primary)">
                  {i18n._("Set new password")}
                </h1>
                <p className="text-sm text-(--color-text-secondary)">
                  {i18n._("Please create a new password for your account")}
                </p>
              </div>
              <form
                onSubmit={newPasswordForm.handleSubmit(onNewPasswordSubmit)}
                className="space-y-4"
                noValidate
              >
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-(--color-text-secondary)">
                      {i18n._("First name")}
                    </Label>
                    <Input
                      id="firstName"
                      type="text"
                      className="bg-(--color-surface-content) border-(--color-border) text-(--color-text-primary) placeholder:text-(--color-text-secondary) focus:border-(--color-border-strong) focus:ring-(--color-ring)"
                      {...newPasswordForm.register("firstName")}
                      disabled={isPending}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-(--color-text-secondary)">
                      {i18n._("Last name")}
                    </Label>
                    <Input
                      id="lastName"
                      type="text"
                      className="bg-(--color-surface-content) border-(--color-border) text-(--color-text-primary) placeholder:text-(--color-text-secondary) focus:border-(--color-border-strong) focus:ring-(--color-ring)"
                      {...newPasswordForm.register("lastName")}
                      disabled={isPending}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-(--color-text-secondary)">
                    {i18n._("New password")}
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      className="bg-(--color-surface-content) border-(--color-border) text-(--color-text-primary) placeholder:text-(--color-text-secondary) focus:border-(--color-border-strong) focus:ring-(--color-ring) pr-10"
                      {...newPasswordForm.register("newPassword")}
                      disabled={isPending}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-(--color-text-secondary) hover:text-(--color-text-secondary)"
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {newPasswordForm.formState.errors.newPassword && (
                    <p className="text-sm text-(--color-danger-text)">
                      {newPasswordForm.formState.errors.newPassword.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-(--color-text-secondary)">
                    {i18n._("Confirm password")}
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    className="bg-(--color-surface-content) border-(--color-border) text-(--color-text-primary) placeholder:text-(--color-text-secondary) focus:border-(--color-border-strong) focus:ring-(--color-ring)"
                    {...newPasswordForm.register("confirmPassword")}
                    disabled={isPending}
                  />
                  {newPasswordForm.formState.errors.confirmPassword && (
                    <p className="text-sm text-(--color-danger-text)">
                      {newPasswordForm.formState.errors.confirmPassword.message}
                    </p>
                  )}
                </div>
                <div className="text-xs text-(--color-text-secondary) space-y-1">
                  <p>{i18n._("Password requirements:")}</p>
                  <ul className="list-disc list-inside space-y-0.5 text-(--color-text-secondary)">
                    <li>{i18n._("At least 12 characters")}</li>
                    <li>{i18n._("One uppercase & lowercase letter")}</li>
                    <li>{i18n._("One number & special character")}</li>
                  </ul>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-(--gray-900) text-white hover:bg-(--gray-800) font-medium"
                  disabled={isPending}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {i18n._("Setting password...")}
                    </>
                  ) : (
                    i18n._("Set password")
                  )}
                </Button>
              </form>
              <div className="text-center">
                <button
                  type="button"
                  onClick={goBack}
                  className="text-sm text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
                >
                  {i18n._("← Back to sign in")}
                </button>
              </div>
            </>
          )}

          {/* Forgot Password Step */}
          {step === "forgot_password" && (
            <>
              <div className="space-y-2 text-center lg:text-left">
                <h1 className="text-2xl font-semibold tracking-tight text-(--color-text-primary)">
                  {i18n._("Reset password")}
                </h1>
                <p className="text-sm text-(--color-text-secondary)">
                  {i18n._("Enter your email to receive a reset code")}
                </p>
              </div>
              <form
                onSubmit={forgotPasswordForm.handleSubmit(
                  onForgotPasswordSubmit,
                )}
                className="space-y-4"
                noValidate
              >
                <div className="space-y-2">
                  <Label htmlFor="resetEmail" className="text-(--color-text-secondary)">
                    {i18n._("Email")}
                  </Label>
                  <Input
                    id="resetEmail"
                    type="email"
                    placeholder="name@example.com"
                    className="bg-(--color-surface-content) border-(--color-border) text-(--color-text-primary) placeholder:text-(--color-text-secondary) focus:border-(--color-border-strong) focus:ring-(--color-ring)"
                    {...forgotPasswordForm.register("email")}
                    disabled={isPending}
                  />
                  {forgotPasswordForm.formState.errors.email && (
                    <p className="text-sm text-(--color-danger-text)">
                      {forgotPasswordForm.formState.errors.email.message}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-(--gray-900) text-white hover:bg-(--gray-800) font-medium"
                  disabled={isPending}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {i18n._("Sending...")}
                    </>
                  ) : (
                    i18n._("Send reset code")
                  )}
                </Button>
              </form>
              <div className="text-center">
                <button
                  type="button"
                  onClick={goBack}
                  className="text-sm text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
                >
                  {i18n._("← Back to sign in")}
                </button>
              </div>
            </>
          )}

          {/* Reset Password Confirmation Step */}
          {step === "reset_password" && (
            <>
              <div className="space-y-2 text-center lg:text-left">
                <h1 className="text-2xl font-semibold tracking-tight text-(--color-text-primary)">
                  {i18n._("Create new password")}
                </h1>
                <p className="text-sm text-(--color-text-secondary)">
                  {i18n._("Enter the code sent to your email")}
                </p>
              </div>
              <form
                onSubmit={resetPasswordForm.handleSubmit(onResetPasswordSubmit)}
                className="space-y-4"
                noValidate
              >
                <div className="space-y-2">
                  <Label htmlFor="verificationCode" className="text-(--color-text-secondary)">
                    {i18n._("Verification code")}
                  </Label>
                  <Input
                    id="verificationCode"
                    type="text"
                    placeholder={i18n._("Enter code")}
                    className="bg-(--color-surface-content) border-(--color-border) text-(--color-text-primary) placeholder:text-(--color-text-secondary) focus:border-(--color-border-strong) focus:ring-(--color-ring)"
                    {...resetPasswordForm.register("code")}
                    disabled={isPending}
                  />
                  {resetPasswordForm.formState.errors.code && (
                    <p className="text-sm text-(--color-danger-text)">
                      {resetPasswordForm.formState.errors.code.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="resetNewPassword" className="text-(--color-text-secondary)">
                    {i18n._("New password")}
                  </Label>
                  <div className="relative">
                    <Input
                      id="resetNewPassword"
                      type={showNewPassword ? "text" : "password"}
                      className="bg-(--color-surface-content) border-(--color-border) text-(--color-text-primary) placeholder:text-(--color-text-secondary) focus:border-(--color-border-strong) focus:ring-(--color-ring) pr-10"
                      {...resetPasswordForm.register("newPassword")}
                      disabled={isPending}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-(--color-text-secondary) hover:text-(--color-text-secondary)"
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {resetPasswordForm.formState.errors.newPassword && (
                    <p className="text-sm text-(--color-danger-text)">
                      {resetPasswordForm.formState.errors.newPassword.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="resetConfirmPassword"
                    className="text-(--color-text-secondary)"
                  >
                    {i18n._("Confirm password")}
                  </Label>
                  <Input
                    id="resetConfirmPassword"
                    type="password"
                    className="bg-(--color-surface-content) border-(--color-border) text-(--color-text-primary) placeholder:text-(--color-text-secondary) focus:border-(--color-border-strong) focus:ring-(--color-ring)"
                    {...resetPasswordForm.register("confirmPassword")}
                    disabled={isPending}
                  />
                  {resetPasswordForm.formState.errors.confirmPassword && (
                    <p className="text-sm text-(--color-danger-text)">
                      {
                        resetPasswordForm.formState.errors.confirmPassword
                          .message
                      }
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-(--gray-900) text-white hover:bg-(--gray-800) font-medium"
                  disabled={isPending}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {i18n._("Resetting...")}
                    </>
                  ) : (
                    i18n._("Reset password")
                  )}
                </Button>
              </form>
              <div className="text-center">
                <button
                  type="button"
                  onClick={goBack}
                  className="text-sm text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
                >
                  {i18n._("← Back to sign in")}
                </button>
              </div>
            </>
          )}

          {/* Mobile branding */}
          <div className="lg:hidden pt-8 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="h-6 w-6 rounded-md bg-(--gray-900) flex items-center justify-center">
                <span className="text-white font-bold text-sm">Y</span>
              </div>
              <span className="text-sm font-medium text-(--color-text-secondary)">YesChef</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
